import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../services/supabase';
import { useProfile } from '../hooks/useProfile';

const C = {
  navy: '#0A1628', gold: '#C9A84C', black: '#0A0A0A',
  white: '#FFFFFF', surface: '#F5F6F8', border: '#D0D5DD',
  borderLt: '#E8EBF0', text: '#0A0A0A', textSec: '#3D4552',
  muted: '#6B7280', mutedLt: '#9CA3AF',
  success: '#0A7C42', danger: '#C0222A', amber: '#92640A', teal: '#0D6E8A',
};

interface DDQAccess {
  id: string; ddq_id: string; status: string; requested_at: string;
  granted_at: string | null; ddq_type: string; ddq_asset_class: string;
  firm_name: string; fund_name: string | null; allow_pdf: boolean; unanswered_questions: number;
}
interface AvailableDDQ {
  ddq_id: string; firm_id: string; firm_name: string;
  ddq_type: string; ddq_asset_class: string; fund_name: string | null;
}

const classLabels: Record<string, string> = {
  hedge_fund: 'Hedge Fund', pe: 'Private Equity', vc: 'Venture Capital',
  real_estate: 'Real Estate', credit: 'Credit', infrastructure: 'Infrastructure',
};
const classColors: Record<string, { color: string; bg: string }> = {
  hedge_fund:     { color: '#0D6E8A', bg: '#EFF8FB' },
  pe:             { color: '#92640A', bg: '#FDF6E7' },
  vc:             { color: '#1D4ED8', bg: '#EFF4FF' },
  real_estate:    { color: '#B91C1C', bg: '#FEF2F2' },
  credit:         { color: '#0F766E', bg: '#F0FDFA' },
  infrastructure: { color: '#6D28D9', bg: '#F5F3FF' },
};

const card: React.CSSProperties = {
  background: C.white, border: `1px solid ${C.border}`,
  borderRadius: '4px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
};

export default function AllocatorDDQ() {
  const navigate = useNavigate();
  const { firm } = useProfile();
  const [ddqs, setDdqs]               = useState<DDQAccess[]>([]);
  const [availableDDQs, setAvailableDDQs] = useState<AvailableDDQ[]>([]);
  const [loading, setLoading]         = useState(true);
  const [activeTab, setActiveTab]     = useState<'granted' | 'requested' | 'available'>('granted');
  const [requesting, setRequesting]   = useState<string | null>(null);

  useEffect(() => { if (firm) fetchData(); }, [firm]);

  const fetchData = async () => {
    if (!firm) return;
    const { data: accessData } = await supabase.from('ddq_access').select('*').eq('allocator_firm_id', firm.id).order('requested_at', { ascending: false });
    const accessedDdqIds = new Set((accessData || []).map(a => a.ddq_id));

    if (accessData) {
      const enriched = await Promise.all(accessData.map(async a => {
        const { data: ddq } = await supabase.from('ddqs').select('*').eq('id', a.ddq_id).single();
        if (!ddq) return null;
        const { data: firmData } = await supabase.from('firms').select('name').eq('id', ddq.firm_id).single();
        let fund_name = null;
        if (ddq.fund_id) { const { data: fundData } = await supabase.from('funds').select('name').eq('id', ddq.fund_id).single(); fund_name = fundData?.name || null; }
        const { count: unansweredCount } = await supabase.from('ddq_questions').select('*', { count: 'exact', head: true }).eq('ddq_id', a.ddq_id).eq('allocator_firm_id', firm.id).is('answered_at', null);
        return { ...a, ddq_type: ddq.type, ddq_asset_class: ddq.asset_class, firm_name: firmData?.name || 'Unknown', fund_name, allow_pdf: ddq.allow_pdf, unanswered_questions: unansweredCount || 0 };
      }));
      setDdqs(enriched.filter(Boolean) as DDQAccess[]);
    }

    const { data: connections } = await supabase.from('connections').select('manager_firm_id').eq('allocator_firm_id', firm.id).eq('status', 'approved');
    if (connections?.length) {
      const { data: submittedDDQs } = await supabase.from('ddqs').select('id, firm_id, type, asset_class, fund_id').in('firm_id', connections.map(c => c.manager_firm_id)).eq('status', 'submitted');
      if (submittedDDQs) {
        const notAccessed = submittedDDQs.filter(d => !accessedDdqIds.has(d.id));
        const enriched = await Promise.all(notAccessed.map(async ddq => {
          const { data: firmData } = await supabase.from('firms').select('name').eq('id', ddq.firm_id).single();
          let fund_name = null;
          if (ddq.fund_id) { const { data: fundData } = await supabase.from('funds').select('name').eq('id', ddq.fund_id).single(); fund_name = fundData?.name || null; }
          return { ddq_id: ddq.id, firm_id: ddq.firm_id, firm_name: firmData?.name || 'Unknown', ddq_type: ddq.type, ddq_asset_class: ddq.asset_class, fund_name };
        }));
        setAvailableDDQs(enriched);
      }
    }
    setLoading(false);
  };

  const handleRequestAccess = async (ddqId: string) => {
    if (!firm) return;
    setRequesting(ddqId);
    await supabase.from('ddq_access').upsert({ ddq_id: ddqId, allocator_firm_id: firm.id, status: 'requested' }, { onConflict: 'ddq_id,allocator_firm_id' });
    setRequesting(null);
    fetchData();
  };

  const grantedDDQs   = ddqs.filter(d => d.status === 'granted');
  const requestedDDQs = ddqs.filter(d => d.status === 'requested');

  if (loading) return (
    <div style={{ padding: '32px', background: C.surface, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ color: C.muted, fontSize: '13px', fontFamily: 'system-ui' }}>Loading...</div>
    </div>
  );

  const tabs = [
    { key: 'granted',   label: `Accessible`,       count: grantedDDQs.length   },
    { key: 'available', label: `Request Access`,    count: availableDDQs.length },
    { key: 'requested', label: `Pending`,           count: requestedDDQs.length },
  ];

  const EmptyState = ({ message }: { message: string }) => (
    <div style={{ ...card, padding: '60px', textAlign: 'center' }}>
      <div style={{ fontSize: '14px', fontWeight: 500, color: C.text, fontFamily: 'Georgia, serif', marginBottom: '6px' }}>No items</div>
      <div style={{ fontSize: '13px', color: C.muted, lineHeight: 1.6, maxWidth: '360px', margin: '0 auto' }}>{message}</div>
    </div>
  );

  return (
    <div style={{ padding: '28px 32px', background: C.surface, minHeight: '100vh', fontFamily: 'system-ui, -apple-system, sans-serif' }}>

      {/* ── Header ── */}
      <div style={{ marginBottom: '20px', paddingBottom: '18px', borderBottom: `1px solid ${C.border}` }}>
        <h1 style={{ fontFamily: 'Georgia, serif', fontSize: '24px', color: C.black, fontWeight: 400, margin: '0 0 4px', letterSpacing: '-0.3px' }}>DDQ Library</h1>
        <p style={{ fontSize: '13px', color: C.muted, margin: 0 }}>Due diligence questionnaires from managers you are connected with</p>
      </div>

      {/* ── Stats ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '20px' }}>
        {[
          { label: 'DDQs with access',    value: grantedDDQs.length,   accent: C.navy    },
          { label: 'Pending requests',    value: requestedDDQs.length, accent: C.amber   },
          { label: 'Available to request',value: availableDDQs.length, accent: C.teal    },
          { label: 'Awaiting answers',    value: grantedDDQs.reduce((s, d) => s + d.unanswered_questions, 0), accent: C.gold },
        ].map((stat, i) => (
          <div key={i} style={{ ...card, padding: '16px 18px', borderLeft: `3px solid ${stat.accent}` }}>
            <div style={{ fontSize: '10px', fontWeight: 700, color: C.muted, letterSpacing: '0.6px', textTransform: 'uppercase', marginBottom: '8px' }}>{stat.label}</div>
            <div style={{ fontFamily: 'Georgia, serif', fontSize: '28px', color: C.navy, lineHeight: 1 }}>{stat.value}</div>
          </div>
        ))}
      </div>

      {/* ── Tabs ── */}
      <div style={{ display: 'flex', gap: '0', marginBottom: '16px', border: `1px solid ${C.border}`, borderRadius: '3px', width: 'fit-content', overflow: 'hidden' }}>
        {tabs.map((tab, i) => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key as any)} style={{
            padding: '7px 16px', fontSize: '12px', cursor: 'pointer', border: 'none',
            background: activeTab === tab.key ? C.navy : C.white,
            color: activeTab === tab.key ? C.white : C.textSec,
            fontFamily: 'system-ui', fontWeight: 500,
            borderRight: i < tabs.length - 1 ? `1px solid ${C.border}` : 'none',
            transition: 'all 0.1s',
          }}>
            {tab.label}
            <span style={{ marginLeft: '6px', fontSize: '10px', fontWeight: 700, background: activeTab === tab.key ? 'rgba(255,255,255,0.2)' : C.surface, padding: '1px 5px', borderRadius: '2px', color: activeTab === tab.key ? C.white : C.muted }}>
              {tab.count}
            </span>
          </button>
        ))}
      </div>

      {/* ── Granted DDQs ── */}
      {activeTab === 'granted' && (
        grantedDDQs.length === 0
          ? <EmptyState message="Request DDQ access from connected managers in the Request Access tab." />
          : <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {grantedDDQs.map(ddq => {
                const cls = classColors[ddq.ddq_asset_class] || { color: C.muted, bg: C.surface };
                return (
                  <div key={ddq.id} onClick={() => navigate(`/ddq/view/${ddq.ddq_id}`)} style={{
                    ...card, padding: '16px 18px', cursor: 'pointer',
                    borderLeft: `3px solid ${cls.color}`, transition: 'box-shadow 0.1s',
                  }}
                    onMouseEnter={e => (e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)')}
                    onMouseLeave={e => (e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.05)')}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '5px', flexWrap: 'wrap' }}>
                          <span style={{ fontSize: '14px', fontWeight: 600, color: C.navy }}>{ddq.firm_name}</span>
                          <span style={{ fontSize: '9px', fontWeight: 700, padding: '2px 6px', borderRadius: '2px', background: cls.bg, color: cls.color, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                            {classLabels[ddq.ddq_asset_class]}
                          </span>
                          <span style={{ fontSize: '9px', fontWeight: 700, padding: '2px 6px', borderRadius: '2px', background: C.surface, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                            {ddq.ddq_type === 'firm' ? 'Firm DDQ' : 'Fund DDQ'}
                          </span>
                          {ddq.allow_pdf && (
                            <span style={{ fontSize: '9px', fontWeight: 700, padding: '2px 6px', borderRadius: '2px', background: '#EFF8FB', color: C.teal, textTransform: 'uppercase', letterSpacing: '0.4px' }}>PDF available</span>
                          )}
                        </div>
                        {ddq.fund_name && <div style={{ fontSize: '12px', color: C.muted, marginBottom: '3px' }}>{ddq.fund_name}</div>}
                        <div style={{ fontSize: '11px', color: C.mutedLt }}>
                          Access granted {ddq.granted_at ? new Date(ddq.granted_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : ''}
                          {ddq.unanswered_questions > 0 && (
                            <span style={{ color: C.amber, marginLeft: '10px', fontWeight: 600 }}>
                              · {ddq.unanswered_questions} question{ddq.unanswered_questions !== 1 ? 's' : ''} awaiting response
                            </span>
                          )}
                        </div>
                      </div>
                      <div style={{ fontSize: '12px', color: C.navy, fontWeight: 600, flexShrink: 0, marginLeft: '16px' }}>View DDQ →</div>
                    </div>
                  </div>
                );
              })}
            </div>
      )}

      {/* ── Available DDQs ── */}
      {activeTab === 'available' && (
        availableDDQs.length === 0
          ? <EmptyState message="Connected managers haven't submitted any DDQs yet, or you've already requested access to all of them." />
          : <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {availableDDQs.map(ddq => {
                const cls = classColors[ddq.ddq_asset_class] || { color: C.muted, bg: C.surface };
                return (
                  <div key={ddq.ddq_id} style={{ ...card, padding: '14px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                        <span style={{ fontSize: '13px', fontWeight: 600, color: C.navy }}>{ddq.firm_name}</span>
                        <span style={{ fontSize: '9px', fontWeight: 700, padding: '2px 6px', borderRadius: '2px', background: cls.bg, color: cls.color, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                          {classLabels[ddq.ddq_asset_class]}
                        </span>
                        <span style={{ fontSize: '9px', fontWeight: 700, padding: '2px 6px', borderRadius: '2px', background: C.surface, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                          {ddq.ddq_type === 'firm' ? 'Firm DDQ' : 'Fund DDQ'}
                        </span>
                      </div>
                      {ddq.fund_name && <div style={{ fontSize: '12px', color: C.muted }}>{ddq.fund_name}</div>}
                    </div>
                    <button onClick={() => handleRequestAccess(ddq.ddq_id)} disabled={requesting === ddq.ddq_id} style={{
                      background: C.navy, border: 'none', borderRadius: '3px',
                      color: C.white, fontSize: '12px', fontWeight: 600,
                      padding: '7px 16px', cursor: requesting === ddq.ddq_id ? 'not-allowed' : 'pointer',
                      fontFamily: 'system-ui', flexShrink: 0, opacity: requesting === ddq.ddq_id ? 0.7 : 1,
                    }}>{requesting === ddq.ddq_id ? 'Requesting...' : 'Request Access'}</button>
                  </div>
                );
              })}
            </div>
      )}

      {/* ── Requested DDQs ── */}
      {activeTab === 'requested' && (
        requestedDDQs.length === 0
          ? <EmptyState message="No pending DDQ requests." />
          : <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {requestedDDQs.map(ddq => (
                <div key={ddq.id} style={{ ...card, padding: '14px 18px', borderLeft: `3px solid ${C.amber}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: C.text, marginBottom: '3px' }}>{ddq.firm_name}</div>
                    <div style={{ fontSize: '11px', color: C.muted }}>
                      {ddq.ddq_type === 'firm' ? 'Firm DDQ' : `Fund DDQ, ${ddq.fund_name}`}
                      {' · Requested '}{new Date(ddq.requested_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                    </div>
                  </div>
                  <span style={{ fontSize: '10px', fontWeight: 700, padding: '3px 10px', borderRadius: '2px', background: '#FFFBEB', color: C.amber, border: '1px solid #FDE68A', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Pending Approval
                  </span>
                </div>
              ))}
            </div>
      )}
    </div>
  );
}